import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import { useAppSelector } from "../../app/hooks";
import { selectAccessToken } from "../../features/auth/authSlice";
import useApiRequest from "../../helpers/api_request";
import { BASE_URL } from "../../helpers/constants";
import { formatDate } from "../../helpers/commons";
import AppSpinner from "../elements/AppSpinner";
import {
  TypeEventsTicketItem,
  TypeTicketType,
} from "../../features/eventsticketitems/eventsTicketItemsSlice";



type TypeCollectionLine = {
  qty: number;
  unit_price: number;
  ticketType?: { name: string };
};

type TypeEventCollection = {
  id: number | string;
  reference: string;
  names: string | null;
  email: string | null;
  phone_number: string | null;
  amount: number;
  currency: string;
  payment_method: string | null;
  created_at: string;
  lines?: TypeCollectionLine[];
  collection?: {
    transaction_id: string;
    status: string;
    charge: number | null;
    customer?: { names: string | null };
  } | null;
};

type TypeAttendee = {
  id: number | string;
  code: string;
  holder_name: string | null;
  status: string;
  checked_in_at: string | null;
  ticketType?: { name: string };
  collectionOrder?: {
    reference: string;
    phone_number: string | null;
    names: string | null;
  };
};

/* ------------------------------------------------------------------ */
/* helpers                                                             */
/* ------------------------------------------------------------------ */

const money = (value: number | null | undefined, currency = "UGX"): string =>
  currency + " " + (Number(value) || 0).toLocaleString();

const shortDate = (value?: string | null): string => {
  if (!value) return "—";
  const date = new Date(value);
  if (isNaN(date.getTime())) return "—";
  return formatDate(value, "MMM DD, YYYY");
};

const formatTime = (value?: string | null): string => {
  if (!value) return "";
  const [hourPart, minutePart] = value.split(":");
  const hour24 = parseInt(hourPart, 10);
  if (isNaN(hour24)) return "";
  const suffix = hour24 < 12 ? "AM" : "PM";
  let hour12 = hour24 % 12;
  if (hour12 === 0) hour12 = 12;
  return hour12 + ":" + (minutePart || "00") + " " + suffix;
};

const isMultiDay = (item: TypeEventsTicketItem): boolean => {
  if (!item.event_date || !item.end_date) return false;
  return (
    String(item.event_date).split("T")[0] !== String(item.end_date).split("T")[0]
  );
};

/** two-letter monogram for the attendee rows */
const initials = (name?: string | null): string => {
  if (!name) return "??";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

/* ------------------------------------------------------------------ */

type EventDetailsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  event: TypeEventsTicketItem | null;
};

type TypeSection = "summary" | "attendees" | "sales" | "tickets" | "checkin";

const EventDetailsModal = ({
  isOpen,
  onClose,
  event,
}: EventDetailsModalProps) => {
  const [section, setSection] = useState<TypeSection>("summary");

  const [collections, setCollections] = useState<TypeEventCollection[]>([]);
  const [isLoadingCollections, setIsLoadingCollections] = useState(false);
  const [hasLoadedCollections, setHasLoadedCollections] = useState(false);
  const [cursor, setCursor] = useState<number | string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  const [attendees, setAttendees] = useState<TypeAttendee[]>([]);
  const [attendeeStats, setAttendeeStats] = useState({
    issued: 0,
    checkedIn: 0,
    expected: 0,
  });
  const [isLoadingAttendees, setIsLoadingAttendees] = useState(false);
  const [hasLoadedAttendees, setHasLoadedAttendees] = useState(false);
  const [attendeeCursor, setAttendeeCursor] = useState<number | string | null>(
    null
  );
  const [attendeesHaveMore, setAttendeesHaveMore] = useState(false);
  const [attendeeFilter, setAttendeeFilter] = useState<
    "all" | "checked_in" | "not_arrived"
  >("all");
  const [attendeeSearch, setAttendeeSearch] = useState("");

  const [gateTerm, setGateTerm] = useState("");
  const [gateResults, setGateResults] = useState<any[]>([]);
  const [isSearchingGate, setIsSearchingGate] = useState(false);
  const [hasSearchedGate, setHasSearchedGate] = useState(false);
  const [checkingCode, setCheckingCode] = useState<string | null>(null);
  const [gateMessage, setGateMessage] = useState<{
    ok: boolean;
    text: string;
  } | null>(null);

  const apiRequest = useApiRequest();
  const accessToken = useAppSelector(selectAccessToken);
  const landingUrl = process.env.REACT_APP_LANDING_URL;
  const storageUrl = process.env.REACT_APP_STORAGE_URL;

  useEffect(() => {
    if (!isOpen) return;
    setSection("summary");
    setCollections([]);
    setHasLoadedCollections(false);
    setCursor(null);
    setHasMore(false);
    setAttendees([]);
    setHasLoadedAttendees(false);
    setAttendeeCursor(null);
    setAttendeesHaveMore(false);
    setAttendeeFilter("all");
    setAttendeeSearch("");
    setGateTerm("");
    setGateResults([]);
    setHasSearchedGate(false);
    setGateMessage(null);
  }, [isOpen, event?.ref]);

  useEffect(() => {
    if (!isOpen || !event) return;
    if (section === "sales" && !hasLoadedCollections) loadCollections();
    if (section === "attendees" && !hasLoadedAttendees) loadAttendees();
  }, [section, isOpen, event?.ref]);

  const loadCollections = async (nextCursor: number | string | null = null) => {
    if (!event) return;
    setIsLoadingCollections(true);

    try {
      const response = await apiRequest({
        method: "get",
        url:
          BASE_URL + "/api/donation-link/event_ticket_collections/" + event.ref,
        headers: { Authorization: "Bearer " + accessToken },
        params: nextCursor != null ? { cursor: nextCursor } : {},
      });

      const payload = response.data;
      const incoming = payload?.items ?? payload ?? [];

      setCollections((existing) =>
        nextCursor === null ? incoming : [...existing, ...incoming]
      );
      setCursor(payload?.nextCursor ?? null);
      setHasMore(
        payload?.nextCursor != null &&
          String(payload.nextCursor) !== String(payload.lastCursor)
      );
      setHasLoadedCollections(true);
    } catch (error) {
      showError(error, "Could not load sales");
    } finally {
      setIsLoadingCollections(false);
    }
  };

  const loadAttendees = async (nextCursor: number | string | null = null) => {
    if (!event) return;
    setIsLoadingAttendees(true);

    try {
      const response = await apiRequest({
        method: "get",
        url: BASE_URL + "/api/donation-link/event_ticket_attendees/" + event.ref,
        headers: { Authorization: "Bearer " + accessToken },
        params: nextCursor != null ? { cursor: nextCursor } : {},
      });

      const payload = response.data;
      const incoming = payload?.items ?? [];

      setAttendees((existing) =>
        nextCursor === null ? incoming : [...existing, ...incoming]
      );
      if (payload?.stats) setAttendeeStats(payload.stats);
      setAttendeeCursor(payload?.nextCursor ?? null);
      setAttendeesHaveMore(
        payload?.nextCursor != null &&
          String(payload.nextCursor) !== String(payload.lastCursor)
      );
      setHasLoadedAttendees(true);
    } catch (error) {
      showError(error, "Could not load attendees");
    } finally {
      setIsLoadingAttendees(false);
    }
  };

  /* the gate searches the whole event server side, so staff can find
     someone by name without loading every attendee first */
  const searchGate = async () => {
    if (!event || gateTerm.trim() === "") return;
    setIsSearchingGate(true);
    setGateMessage(null);

    try {
      const response = await apiRequest({
        method: "get",
        url: BASE_URL + "/api/donation-link/event_attendee_search/" + event.ref,
        headers: { Authorization: "Bearer " + accessToken },
        params: { q: gateTerm.trim() },
      });

      setGateResults(response.data || []);
      setHasSearchedGate(true);
    } catch (error) {
      showError(error, "Search failed");
    } finally {
      setIsSearchingGate(false);
    }
  };

  const admit = async (order: any) => {
    if (!event) return;
    setCheckingCode(String(order.id));

    try {
      const response = await apiRequest({
        method: "post",
        url: BASE_URL + "/api/donation-link/event_ticket_checkin/" + event.ref,
        headers: { Authorization: "Bearer " + accessToken },
        data: { order_id: order.id },
      });

      const result = response.data;
      setGateMessage({ ok: result?.ok === true, text: result?.message || "" });

      // reflect the new state without a second round trip
      if (result?.ok && result?.ticket) {
        setGateResults((rows) =>
          rows.map((row) =>
            String(row.id) === String(order.id)
              ? { ...row, _admitted: (row._admitted || 0) + 1 }
              : row
          )
        );
        setAttendeeStats((stats) => ({
          ...stats,
          checkedIn: stats.checkedIn + 1,
          expected: Math.max(stats.expected - 1, 0),
        }));
        // the attendees list is now stale
        setHasLoadedAttendees(false);
      }
    } catch (error) {
      showError(error, "Check in failed");
    } finally {
      setCheckingCode(null);
    }
  };

  /**
   * A plain <a href> navigates without the Authorization header, so any
   * protected export comes back 401. Fetch it with the token instead and
   * hand the browser a blob from memory.
   */
  const downloadFile = async (url: string, filename: string, mime: string) => {
    try {
      const response = await apiRequest({
        method: "get",
        url,
        headers: { Authorization: "Bearer " + accessToken },
        responseType: "blob",
      });

      // useApiRequest may unwrap to the body already, so accept either
      const body = (response as any)?.data ?? response;

      const blobUrl = window.URL.createObjectURL(
        new Blob([body], { type: mime })
      );

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      link.click();

      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      showError(error, "Download failed");
    }
  };

  const showError = (error: unknown, fallback: string) => {
    const errorResponse = error as AxiosError;
    if (errorResponse.response) {
      const data = errorResponse.response.data as { message: string };
      toast.error(data.message, { position: toast.POSITION.TOP_RIGHT });
    } else {
      toast.error(fallback, { position: toast.POSITION.TOP_RIGHT });
    }
  };

  const copyLink = (value: string) => {
    navigator.clipboard.writeText(value);
    toast.success("Copied", { position: toast.POSITION.TOP_RIGHT });
  };

  if (!isOpen || !event) return null;

  const types = event.ticket_types || [];
  const totalSold = types.reduce((sum, t) => sum + (Number(t.sold) || 0), 0);
  const totalCollected = types.reduce(
    (sum, t) => sum + (Number(t.sold) || 0) * (Number(t.price) || 0),
    0
  );
  const totalCapacity = types.reduce(
    (sum, t) => sum + (Number(t.capacity) || 0),
    0
  );
  const allCapped = types.length > 0 && types.every((t) => Number(t.capacity) > 0);
  const soldPercent =
    allCapped && totalCapacity > 0
      ? Math.min(Math.round((totalSold / totalCapacity) * 100), 100)
      : null;

  const publicUrl = landingUrl + "/ticket/" + event.ref;

  const query = attendeeSearch.trim().toLowerCase();
  const visibleAttendees = attendees.filter((person) => {
    if (attendeeFilter === "checked_in" && !person.checked_in_at) return false;
    if (attendeeFilter === "not_arrived" && person.checked_in_at) return false;
    if (query === "") return true;

    return [
      person.holder_name,
      person.collectionOrder?.names,
      person.collectionOrder?.phone_number,
      person.collectionOrder?.reference,
      person.code,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase()
      .includes(query);
  });

  const nav: { key: TypeSection; label: string; hint: string }[] = [
    { key: "summary", label: "Summary", hint: "Sales at a glance" },
    { key: "attendees", label: "Attendees", hint: "Who is coming" },
    { key: "sales", label: "Sales", hint: "Every purchase" },
    { key: "tickets", label: "Ticket types", hint: "How each is doing" },
    { key: "checkin", label: "Gate check in", hint: "Admit by code" },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center px-4 py-8 overflow-y-auto">
      <div
        className="fixed inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      ></div>

      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex sm:flex-col">
        {/* ================= left rail ================= */}
        <div className="w-64 sm:w-full bg-gray-50 border-r sm:border-r-0 sm:border-b flex flex-col">
          <div className="px-5 pt-5 pb-4 border-b">
            <div
              className="h-24 w-full rounded-lg bg-gray-200 mb-3"
              style={{
                backgroundImage: event.image
                  ? `url(${storageUrl}/${event.image})`
                  : "none",
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            ></div>

            {event.tagline && (
              <p className="text-[10px] uppercase tracking-widest text-gray-400">
                {event.tagline}
              </p>
            )}
            <p className="font-bold leading-tight mt-0.5">{event.title}</p>
            <p className="text-xs text-gray-500 mt-1">
              {shortDate(event.event_date)}
              {isMultiDay(event) ? " – " + shortDate(event.end_date) : ""}
            </p>
          </div>

          <div className="py-2 flex-grow sm:flex sm:overflow-x-auto">
            {nav.map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => setSection(item.key)}
                className={
                  "w-full sm:w-auto text-left px-5 py-2.5 border-l-2 sm:border-l-0 sm:border-b-2 transition-colors whitespace-nowrap " +
                  (section === item.key
                    ? "border-prim-yellow bg-white"
                    : "border-transparent hover:bg-gray-100")
                }
              >
                <span
                  className={
                    "block text-sm " +
                    (section === item.key
                      ? "font-semibold text-gray-900"
                      : "text-gray-600")
                  }
                >
                  {item.label}
                </span>
                <span className="block text-[11px] text-gray-400 sm:hidden">
                  {item.hint}
                </span>
              </button>
            ))}
          </div>

          <div className="px-5 py-4 border-t sm:hidden">
            <Link
              to={`/events-tickets/edit/${event.ref}`}
              className="prim-btn-1 w-full text-center block text-xs py-2"
            >
              Edit event
            </Link>
            <button
              type="button"
              onClick={() => copyLink(publicUrl)}
              className="btn-light w-full mt-2 text-xs py-2"
            >
              Copy ticket link
            </button>
          </div>
        </div>

        {/* ================= right pane ================= */}
        <div className="flex-grow overflow-y-auto max-h-[90vh]">
          <div className="flex items-start px-6 pt-5">
            <div className="flex-grow">
              <p className="text-lg font-bold">
                {nav.find((n) => n.key === section)?.label}
              </p>
              <p className="text-xs text-gray-500">
                {nav.find((n) => n.key === section)?.hint}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              title="Close"
              className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
            >
              &times;
            </button>
          </div>

          {/* ---------------- summary ---------------- */}
          {section === "summary" && (
            <div className="px-6 py-5">
              {/* a single figure carries the page, rather than a row of
                  equal-weight stat boxes */}
              <p className="text-3xl font-bold">{money(totalCollected)}</p>
              <p className="text-xs text-gray-500">
                collected from {totalSold.toLocaleString()} ticket
                {totalSold === 1 ? "" : "s"}
              </p>

              {soldPercent !== null && (
                <div className="mt-4">
                  <div className="flex text-xs mb-1">
                    <span className="flex-grow text-gray-500">
                      {totalSold.toLocaleString()} of{" "}
                      {totalCapacity.toLocaleString()} sold
                    </span>
                    <span className="font-medium">{soldPercent}%</span>
                  </div>
                  <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-prim-yellow rounded-full"
                      style={{ width: soldPercent + "%" }}
                    ></div>
                  </div>
                </div>
              )}

              <div className="divide-y border-t mt-6">
                {[
                  {
                    label: "Where",
                    value:
                      event.event_mode === "online"
                        ? (event.venue || "Online") + " (online)"
                        : event.venue || "—",
                  },
                  {
                    label: "Runs",
                    value:
                      shortDate(event.event_date) +
                      (isMultiDay(event)
                        ? " to " + shortDate(event.end_date)
                        : "") +
                      (event.start_time
                        ? ", " +
                          formatTime(event.start_time) +
                          (event.end_time
                            ? " – " + formatTime(event.end_time)
                            : " till late")
                        : ""),
                  },
                  { label: "Category", value: event.category || "—" },
                  {
                    label: "Visibility",
                    value: event.type
                      ? event.type.charAt(0).toUpperCase() + event.type.slice(1)
                      : "—",
                  },
                  { label: "Created", value: shortDate(event.created_at) },
                ].map((row, key) => (
                  <div key={key} className="flex py-2.5 text-sm">
                    <span className="w-32 text-gray-400 shrink-0">
                      {row.label}
                    </span>
                    <span className="text-gray-800">{row.value}</span>
                  </div>
                ))}
              </div>

              <div className="border-t pt-4 mt-4">
                <p className="text-xs text-gray-400">Ticket link</p>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-sm break-all flex-grow">{publicUrl}</p>
                  <button
                    type="button"
                    onClick={() => copyLink(publicUrl)}
                    className="btn-light text-xs py-1.5 shrink-0"
                  >
                    Copy
                  </button>
                  <a
                    href={publicUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-light text-xs py-1.5 shrink-0"
                  >
                    Open
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* ---------------- attendees ---------------- */}
          {section === "attendees" && (
            <div className="px-6 py-5">
              {/* headline reads as a sentence rather than three stat cards */}
              <div className="flex items-start">
                <p className="text-sm flex-grow">
                  <span className="text-2xl font-bold">
                    {attendeeStats.checkedIn.toLocaleString()}
                  </span>
                  <span className="text-gray-500">
                    {" "}
                    of {attendeeStats.issued.toLocaleString()} arrived
                  </span>
                </p>

                {/* a printable manifest - one row per seat, with codes */}
                <button
                  type="button"
                  onClick={() =>
                    downloadFile(
                      BASE_URL +
                        "/api/donation-link/event_attendees_download/" +
                        event.ref,
                      event.ref + "-attendees.csv",
                      "text/csv;charset=utf-8;"
                    )
                  }
                  className="btn-light text-xs shrink-0"
                >
                  Download Report
                </button>
              </div>

              <div className="flex items-center gap-2 mt-4">
                {/* segmented control, not chips - shrink-0 so the labels
                    never get squeezed by the search box beside them */}
                <div className="inline-flex rounded-lg border overflow-hidden shrink-0">
                  {[
                    { key: "all", label: "All" },
                    { key: "checked_in", label: "Arrived" },
                    { key: "not_arrived", label: "Expected" },
                  ].map((tab) => (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() => setAttendeeFilter(tab.key as any)}
                      className={
                        "text-xs px-3 py-1.5 border-r last:border-r-0 whitespace-nowrap " +
                        (attendeeFilter === tab.key
                          ? "bg-gray-900 text-white"
                          : "bg-white text-gray-600 hover:bg-gray-50")
                      }
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                <input
                  value={attendeeSearch}
                  onChange={(e) => setAttendeeSearch(e.target.value)}
                  type="text"
                  placeholder="Search..."
                  className="form-input-2 text-xs w-40 min-w-0"
                />
              </div>

              {isLoadingAttendees && <AppSpinner />}

              {!isLoadingAttendees && visibleAttendees.length === 0 && (
                <p className="text-center text-sm text-gray-500 py-10">
                  {attendees.length === 0
                    ? "No tickets issued yet."
                    : "Nobody matches that filter."}
                </p>
              )}

              {/* rows, not a table - avoids the ModulX grid feel */}
              <div className="divide-y mt-3">
                {visibleAttendees.map((person, key) => {
                  const name =
                    person.holder_name ||
                    person.collectionOrder?.names ||
                    "Unnamed";

                  return (
                    <div key={key} className="flex items-center gap-3 py-3">
                      <div
                        className={
                          "h-9 w-9 rounded-full shrink-0 flex items-center justify-center text-[11px] font-bold " +
                          (person.checked_in_at
                            ? "bg-prim-yellow text-black"
                            : "bg-gray-100 text-gray-500")
                        }
                      >
                        {initials(name)}
                      </div>

                      <div className="min-w-0 flex-grow">
                        <p className="text-sm text-gray-800 font-medium truncate">
                          {name}
                        </p>
                        <p className="text-xs text-gray-400 truncate">
                          {person.ticketType?.name}
                          {person.collectionOrder?.phone_number
                            ? " · " + person.collectionOrder.phone_number
                            : ""}
                        </p>
                      </div>

                      <span
                        onClick={() => copyLink(person.code)}
                        title="Copy code"
                        className="font-mono text-xs tracking-widest text-gray-500 cursor-pointer hover:text-gray-900 shrink-0"
                      >
                        {person.code}
                      </span>

                      <span
                        className={
                          "text-[11px] font-medium shrink-0 w-20 text-right " +
                          (person.checked_in_at
                            ? "text-prim-green"
                            : "text-gray-300")
                        }
                      >
                        {person.checked_in_at ? "Arrived" : "Expected"}
                      </span>
                    </div>
                  );
                })}
              </div>

              {attendeesHaveMore && !isLoadingAttendees && (
                <button
                  onClick={() => loadAttendees(attendeeCursor)}
                  type="button"
                  className="btn-light w-full mt-4 text-xs"
                >
                  Load more attendees
                </button>
              )}

              {attendeeSearch !== "" && attendeesHaveMore && (
                <p className="text-xs text-gray-400 text-center mt-2">
                  Searching only what has loaded so far.
                </p>
              )}
            </div>
          )}

          {/* ---------------- sales ---------------- */}
          {section === "sales" && (
            <div className="px-6 py-5">
              <div className="flex">
                <div className="flex-grow"></div>
                <button
                  type="button"
                  onClick={() =>
                    downloadFile(
                      BASE_URL +
                        "/api/donation-link/event_ticket_collections/" +
                        event.ref +
                        "/download",
                      event.ref + "-sales.xlsx",
                      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    )
                  }
                  className="btn-light text-xs"
                >
                  Export
                </button>
              </div>

              {isLoadingCollections && <AppSpinner />}

              {!isLoadingCollections && collections.length === 0 && (
                <p className="text-center text-sm text-gray-500 py-10">
                  No ticket sales yet.
                </p>
              )}

              <div className="divide-y mt-2">
                {collections.map((row, key) => (
                  <div key={key} className="flex items-start gap-3 py-3">
                    <div className="min-w-0 flex-grow">
                      <p className="text-sm text-gray-800 font-medium truncate">
                        {row.collection?.customer?.names || row.names || "—"}
                      </p>
                      <p className="text-xs text-gray-400 truncate">
                        {(row.lines || [])
                          .map(
                            (line) =>
                              `${line.qty} × ${line.ticketType?.name ?? "Ticket"}`
                          )
                          .join(", ")}
                        {row.phone_number ? " · " + row.phone_number : ""}
                      </p>
                      <p className="text-[11px] text-gray-300 mt-0.5">
                        {row.reference}
                        {row.collection?.transaction_id
                          ? " · " + row.collection.transaction_id
                          : ""}
                      </p>
                    </div>

                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold text-gray-800">
                        {money(row.amount, row.currency)}
                      </p>
                      <p className="text-xs text-gray-400">
                        {shortDate(row.created_at)}
                      </p>
                      <p
                        className={
                          "text-[11px] font-medium " +
                          (row.collection?.status === "successful"
                            ? "text-prim-green"
                            : row.collection?.status === "failed"
                            ? "text-red-500"
                            : "text-gray-400")
                        }
                      >
                        {row.collection?.status || "pending"}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {hasMore && !isLoadingCollections && (
                <button
                  onClick={() => loadCollections(cursor)}
                  type="button"
                  className="btn-light w-full mt-4 text-xs"
                >
                  Load more sales
                </button>
              )}
            </div>
          )}

          {/* ---------------- ticket types ---------------- */}
          {section === "tickets" && (
            <div className="px-6 py-5">
              {types.length === 0 && (
                <p className="text-center text-sm text-gray-500 py-10">
                  No tickets on this event.
                </p>
              )}

              <div className="flex flex-col gap-3">
                {types.map((ticket: TypeTicketType, key) => {
                  const sold = Number(ticket.sold) || 0;
                  const capacity = Number(ticket.capacity) || 0;
                  const price = Number(ticket.price) || 0;
                  const percent =
                    capacity > 0
                      ? Math.min(Math.round((sold / capacity) * 100), 100)
                      : null;

                  return (
                    <div key={key} className="border rounded-lg px-4 py-3">
                      <div className="flex items-start">
                        <div className="flex-grow">
                          <p className="text-sm font-semibold text-gray-800">
                            {ticket.name}
                          </p>
                          {ticket.note && (
                            <p className="text-xs text-gray-400">
                              {ticket.note}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold">
                            {price > 0 ? money(price) : "Free"}
                          </p>
                          <p className="text-xs text-gray-400">per person</p>
                        </div>
                      </div>

                      <div className="flex text-xs mt-3 mb-1">
                        <span className="flex-grow text-gray-500">
                          {sold.toLocaleString()} sold
                          {capacity > 0
                            ? " of " + capacity.toLocaleString()
                            : " · unlimited"}
                        </span>
                        <span className="font-medium">
                          {money(sold * price)}
                        </span>
                      </div>

                      {percent !== null && (
                        <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-prim-yellow rounded-full"
                            style={{ width: percent + "%" }}
                          ></div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ---------------- check in ---------------- */}
          {section === "checkin" && (
            <div className="px-6 py-5">
              <p className="text-sm text-gray-500">
                Search by name, phone, order reference or ticket code
              </p>

              <div className="flex gap-2 mt-3">
                <input
                  value={gateTerm}
                  onChange={(e) => setGateTerm(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") searchGate();
                  }}
                  type="text"
                  autoFocus
                  placeholder="e.g. Sarah, 0784..., TK-4W3UR4"
                  className="form-input-2 flex-grow"
                />
                <button
                  type="button"
                  onClick={() => searchGate()}
                  className="prim-btn-1 whitespace-nowrap px-5"
                >
                  Search
                </button>
              </div>

              {gateMessage && (
                <div
                  className={
                    "rounded-lg px-4 py-3 mt-4 text-sm font-medium " +
                    (gateMessage.ok
                      ? "bg-green-50 text-prim-green"
                      : "bg-red-50 text-red-600")
                  }
                >
                  {gateMessage.ok ? "✓ " : "✕ "}
                  {gateMessage.text}
                </div>
              )}

              {isSearchingGate && <AppSpinner />}

              {!isSearchingGate && hasSearchedGate && gateResults.length === 0 && (
                <p className="text-center text-sm text-gray-500 py-10">
                  Nobody found. Check the spelling, or try their phone number.
                </p>
              )}

              <div className="divide-y mt-2">
                {gateResults.map((order, key) => {
                  const name = order.names || "Unnamed";
                  const seats = (order.lines || []).reduce(
                    (sum: number, line: any) => sum + line.qty,
                    0
                  );
                  const alreadyIn =
                    (order.issued || []).filter((t: any) => t.checked_in_at)
                      .length + (order._admitted || 0);
                  const allIn = seats > 0 && alreadyIn >= seats;
                  const paid = order.collection?.status === "successful";

                  return (
                    <div key={key} className="flex items-center gap-3 py-3">
                      <div
                        className={
                          "h-10 w-10 rounded-full shrink-0 flex items-center justify-center text-xs font-bold " +
                          (allIn
                            ? "bg-prim-yellow text-black"
                            : "bg-gray-100 text-gray-500")
                        }
                      >
                        {initials(name)}
                      </div>

                      <div className="min-w-0 flex-grow">
                        <p className="text-sm text-gray-800 font-medium truncate">
                          {name}
                        </p>
                        <p className="text-xs text-gray-400 truncate">
                          {(order.lines || [])
                            .map(
                              (line: any) =>
                                `${line.qty} × ${line.ticketType?.name ?? "Ticket"}`
                            )
                            .join(", ")}
                          {order.phone_number ? " · " + order.phone_number : ""}
                        </p>
                        <p className="text-[11px] text-gray-300">
                          {order.reference}
                          {!paid ? " · payment " + (order.collection?.status ?? "pending") : ""}
                        </p>
                      </div>

                      {/* the count sits above the button so a group order
                          reads as progress rather than a cramped row */}
                      <div className="shrink-0 w-32 text-right">
                        <p
                          className={
                            "text-xs font-semibold mb-1 " +
                            (allIn ? "text-prim-green" : "text-gray-600")
                          }
                        >
                          {allIn
                            ? "All admitted"
                            : seats > 1
                            ? `${seats - alreadyIn} still to come`
                            : "Not yet admitted"}
                        </p>

                        {!allIn && (
                          <button
                            type="button"
                            onClick={() => admit(order)}
                            disabled={checkingCode === String(order.id)}
                            className="prim-btn-1 text-xs px-4 py-2 w-full"
                          >
                            {checkingCode === String(order.id) ? "..." : "Admit"}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {gateResults.length > 0 && (
                <p className="text-xs text-gray-400 mt-4">
                  One order can hold several seats — tap Admit one for each
                  person as they come through.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventDetailsModal;
